import React, { ChangeEvent } from "react";
import Chip from "@mui/material/Chip";
import { equals } from "ramda";
import UserGroupDataModel, { UsersQueryParams } from "../DataModel/UsersDataService";
import { Autocomplete } from '@mui/material';
import { AutocompleteChangeReason, AutocompleteProps } from '@mui/material/Autocomplete';

import { SupervisedUserCircle,
	AccountCircle,
	AccountCircleOutlined,
	Help,
	ErrorRounded,
 } from "@mui/icons-material";

import {  TextField, Tooltip, CircularProgress, Box  } from "@mui/material";

type DisableClearable = Tripple;
type FreeSolo = Tripple;
type Multiple = Tripple;
type Option = { [key: string]: any };
type Tripple = boolean | undefined;

interface AuthorityProps
	extends AutocompleteProps<Option | null, Multiple, DisableClearable, FreeSolo> {
	error: boolean | undefined;
	important: boolean | undefined;
	label: string;
	mandatory: boolean | undefined;
	multiple: Tripple;
	type: string;
	value: string | string[] | undefined;
	helperText: string;
	uniqueKey: string;
	disabled: boolean;
}

interface AuthorityState {
	inputValue: string;
	loading: boolean;
	matchDisabled: boolean;
	matchSystem: boolean;
	options: Option[];
	selectedAuthorities: Option[];
}

let DataService: UserGroupDataModel;

class Authority extends React.PureComponent<AuthorityProps, AuthorityState> {
	constructor(props: AuthorityProps) {
		super(props);

		DataService = new UserGroupDataModel();

		this.state = {
			inputValue: "",
			loading: true,
			matchDisabled: false,
			matchSystem: false,
			options: [],
			selectedAuthorities: [],
		};
	}

	delayTimer: any;

	componentDidMount = () => {
		this.getOptions();
	};

	getOptions = (queryParamsOverride: UsersQueryParams = {}) => {
		const queryParams: UsersQueryParams = {
			filter: this.state.inputValue,
			matchDisabled: this.state.matchDisabled,
			matchSystem: this.state.matchSystem,
			maxItems: 20,
			searchBy: "DISPLAYNAME",
			uniqueKey: this.props.uniqueKey,
			...queryParamsOverride,
		};

		(async () => {
			const usersObjects: any = await DataService.getUserOptions(this.props.value, queryParams);
			const selectedOptions: Option[] = usersObjects ? usersObjects[0].sort(DataService.sortByTypeByName) : [];
			let options: Option[] = usersObjects ? usersObjects[1].sort(DataService.sortByTypeByName) : [];
			options = options.concat(selectedOptions);
			this.setState({
				selectedAuthorities: selectedOptions,
				options,
				loading: false,
			});
		})();
	};

	handleOnChange = (
		event: ChangeEvent<{}>,
		value: Option[] | null,
		action: AutocompleteChangeReason
	) => {
		this.props?.onChange?.(event, value, action);
		this.setState({ inputValue: "" });
	};

	onInputChange = async (event: ChangeEvent) => {
		event.preventDefault();
		const { value } = event.target as HTMLInputElement;
		await this.setState({ inputValue: value ?? "" });
		this.getOptions();
	};

	componentDidUpdate = (prevProps: AuthorityProps, prevState: AuthorityState) => {
		if (!equals(prevProps.value, this.props.value) || this.state.matchDisabled !== prevState.matchDisabled) {
			this.getOptions();
		}
	};

	static defaultProps = {
		multiple: false,
	};

	toggleDisabled = (event: React.MouseEvent) => {
		event.preventDefault();
		this.setState((prev) => ({ matchDisabled: !prev.matchDisabled }));
	};

	renderTags = (value: any, getTagProps: Function) => {
		return value.map((item: any, index: number) => {
			const { type, enabled, deleted } = item;

			const uKeys = ["USER", "cm:person"];
			const gKeys = ["GROUP", "cm:authorityContainer"];

			let icon = null;
			let bgColor = "";

			switch (true) {
				case uKeys.includes(type) && enabled === true:
					icon = <AccountCircle />;
					bgColor = "rgb(162, 218, 255)";
					break;
				case uKeys.includes(type) && enabled === false:
					icon = <AccountCircle />;
					bgColor = "rgb(218, 218, 218)";
					break;
				case gKeys.includes(type):
					icon = <SupervisedUserCircle />;
					bgColor = "rgb(255, 208, 123)";
					break;
				case deleted === true:
					icon = <ErrorRounded />;
					bgColor = "rgb(150, 150, 150)";
					break;
				case type === "ZZNOTFOUND":
					icon = <Help />;
					bgColor = "gray";
					break;
			}

			return (
				<Chip
					key={item?.value}
					avatar={icon}
					label={item?.displayName}
					size="small"
					{...getTagProps({ index })}
					sx={{
						backgroundColor: bgColor,
						justifyContent: "flex-start",
						flexGrow: 1,
						marginLeft: index === 0 ? "2px !important" : undefined,
						"& .MuiChip-label": { flexGrow: 1 },
					}}
				/>
			);
		});
	};

	getGroups = (option: any) => {
		const uKeys = ["USER", "cm:person", "cm:authority"];
		if (uKeys.includes(option.type)) return window.Alfresco.messages.global["edocat.global.users"];
		if (option.type === "ZZNOTFOUND") return window.Alfresco.messages.global["edocat.global.notFound"];
		return window.Alfresco.messages.global["edocat.global.groups"];
	};

	render() {
		const { multiple, type, disabled } = this.props;
		const { loading, selectedAuthorities, inputValue } = this.state;

		return (
			<Autocomplete
				options={this.state.options}
				value={selectedAuthorities}
				inputValue={inputValue}
				multiple={multiple}
				groupBy={this.getGroups}
				onChange={this.handleOnChange}
				popupIcon={""}
				size="small"
				fullWidth
				id="tags-outlined"
				filterSelectedOptions
				isOptionEqualToValue={(option, value) => option[this.props.uniqueKey] === value[this.props.uniqueKey]}
				getOptionLabel={(option) => `${option?.displayName}`}
				renderTags={this.props.multiple ? this.renderTags : undefined}
				disabled={disabled}
				sx={{
					"& .MuiAutocomplete-endAdornment": {
						right: 3,
					},
					"& .MuiAutocomplete-inputRoot": {
						padding: "6px !important",
						paddingRight: "60px !important",
						cursor: "default !important",
					},
					"& input.MuiInputBase-input": {
						padding: "0px 6px !important",
						minWidth: "30% !important",
						flexGrow: 1,
						height: "24px !important",
						margin: "2px !important",
						fontSize: "14px !important",
						backgroundColor: "#EFEFEF !important",
						borderRadius: "12px !important",
					},
				}}
				renderInput={(params: any) => (
					<TextField
						{...params}
						placeholder={window.Alfresco.messages.global["edocat.global.search"]}
						label={this.props?.label ?? ""}
						variant="outlined"
						required={this.props?.mandatory}
						error={!!this.props?.error}
						helperText={
							this.props?.error ||
							this.props?.important ||
							this.props?.helperText
						}
						InputLabelProps={{ shrink: true }}
						onChange={this.onInputChange}
						onKeyDown={(event: any) => {
							if (event.key === "Backspace") event.stopPropagation();
						}}
						InputProps={{
							...params.InputProps,
							sx: {
								...(['USER', 'cm:person', 'cm:authority'].includes(type) && {
									'&.MuiAutocomplete-inputRoot': {
										paddingRight: '100px !important',
									},
								}),
							},
							endAdornment: (
								<Box sx={{
							position: "absolute",
							right: "9px",
							display: "flex",
							alignItems: 'center',
							'& > :last-of-type': {
							marginLeft: 1,
						},
							'& .MuiAutocomplete-endAdornment': {
							position: 'relative',
							display: 'contents',
						}
						}}
								>
									{loading && <CircularProgress color="inherit" size={20} />}
									{params.InputProps.endAdornment}
									{['USER', 'cm:person', 'cm:authority'].includes(type) && (
										<>
											{!this.state.matchDisabled ? (
												<Tooltip title={window.Alfresco.messages.global["edocat.authority-inactive"]}>
													<AccountCircle
														color={disabled ? "disabled" : "primary"}
														style={{ cursor: disabled ? "default" : "pointer" }}
														onClick={!disabled ? this.toggleDisabled : undefined}
													/>
												</Tooltip>
											) : (
												<Tooltip title={window.Alfresco.messages.global["edocat.authority-active"]}>
													<AccountCircleOutlined
														color={disabled ? "disabled" : "primary"}
														style={{ cursor: disabled ? "default" : "pointer" }}
														onClick={!disabled ? this.toggleDisabled : undefined}
													/>
												</Tooltip>
											)}
										</>
									)}
								</Box>
							),
						}}
					/>
				)}
			/>
		);
	}
}

export default Authority;
